import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Newspaper, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { da } from 'date-fns/locale';

interface Props {
  variantId: string;
  modelId: string;
  brandId?: string;
}

interface ArticleRow {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image_url: string | null;
  published_at: string | null;
  match_level: 'variant' | 'model';
}

export function RelatedArticles({ variantId, modelId }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['related-articles', variantId, modelId],
    queryFn: async () => {
      const fetchPosts = async (col: 'variant_id' | 'model_id', value: string) => {
        const { data } = await supabase
          .from('news_post_cars')
          .select('news_post_id, news_posts!inner(id, title, slug, excerpt, cover_image_url, published_at, is_published)')
          .eq(col, value)
          .eq('news_posts.is_published', true);
        return (data || []) as any[];
      };

      const variantRows = await fetchPosts('variant_id', variantId);
      const modelRows = await fetchPosts('model_id', modelId);

      const seen = new Set<string>();
      const out: ArticleRow[] = [];
      const push = (rows: any[], level: ArticleRow['match_level']) => {
        for (const r of rows) {
          const p = r.news_posts;
          if (!p || seen.has(p.id)) continue;
          seen.add(p.id);
          out.push({ ...p, match_level: level });
          if (out.length >= 4) break;
        }
      };
      push(variantRows, 'variant');
      push(modelRows, 'model');
      out.sort((a, b) => new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime());
      return out.slice(0, 3);
    },
  });


  if (isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Newspaper className="w-5 h-5 text-accent" /> Læs om denne bil</CardTitle></CardHeader>
        <CardContent><div className="h-20 bg-secondary/50 rounded animate-pulse" /></CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) return null;

  return (
    <Card id="artikler">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Newspaper className="w-5 h-5 text-accent" /> Læs om denne bil
        </CardTitle>
        <Link to="/nyheder" className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1">
          Alle artikler <ArrowRight className="w-3 h-3" />
        </Link>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {data.map((a) => (
            <li key={a.id}>
              <Link to={`/nyheder/${a.slug}`} className="flex gap-3 -mx-2 p-2 rounded-md hover:bg-secondary/60">
                {a.cover_image_url && (
                  <img src={a.cover_image_url} alt="" loading="lazy" className="w-20 h-14 object-cover rounded flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground line-clamp-2">{a.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {a.published_at ? formatDistanceToNow(new Date(a.published_at), { addSuffix: true, locale: da }) : 'Udkast'}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
